import { useEffect, useCallback, useRef } from 'react';
import { preloadRoute, preloadByPriority } from '../utils/routePreloader';

/**
 * Hook to preload routes on component mount and during idle time
 * @param {boolean} enableIdlePreloading - Whether to enable automatic idle preloading
 */
export const useRoutePreloader = (enableIdlePreloading = true) => {
  const hasPreloaded = useRef(false);

  useEffect(() => {
    if (enableIdlePreloading && !hasPreloaded.current) {
      hasPreloaded.current = true;
      preloadByPriority();
    }
  }, [enableIdlePreloading]);

  /**
   * Manually preload a route
   * @param {string} routeName - The route name to preload
   */
  const preload = useCallback((routeName) => {
    preloadRoute(routeName);
  }, []);

  return { preload };
};

/**
 * Hook to preload a route on hover with debouncing
 * Returns event handlers for onMouseEnter
 */
export const useHoverPreload = (routeName) => {
  const timeoutRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    // Debounce hover - only preload if hover lasts 50ms
    timeoutRef.current = setTimeout(() => {
      preloadRoute(routeName);
    }, 50);
  }, [routeName]);

  const handleMouseLeave = useCallback(() => {
    // Cancel preload if user quickly moves away
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave };
};
